import { Card, CardProps } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useGoogleAddressSearch, UseGoogleAddressSearchConfig, MappedAddress } from '../hooks';
import { GoogleOfficialIcon, SearchIcon } from './icons';
import Autocomplete, { AutocompleteOption } from './Autocomplete';
import { parseGqlError, TranslationFunction } from '../utils';

const styleBox = {
  borderRadius: 'lg',
  w: 'full',
  bg: 'bg.light',
  px: { base: 'md', md: 'xl' },
  pt: 'md',
};

interface AddressSearchProps extends Omit<CardProps, 'onSelect' | 'onChange' | 'onError'> {
  t?: TranslationFunction;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onSelect?: (data: MappedAddress) => void;
  value?: AutocompleteOption | null;
  onChange?: (value: AutocompleteOption | null) => void;
  onError?: (error: string) => void;
  isRequired?: boolean;
  googleAddressConfig: UseGoogleAddressSearchConfig;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  t,
  label,
  placeholder,
  helperText,
  onSelect,
  value,
  onChange,
  onError,
  isRequired,
  googleAddressConfig,
  ...style
}) => {
  const [error, setError] = useState<string>('');

  const { getGoogleAddresses } = useGoogleAddressSearch(googleAddressConfig);

  const handleSearch = async (searchValue: string) => {
    try {
      const data = await getGoogleAddresses({
        input: searchValue,
      });

      setError('');
      onError?.('');

      return data
        ? data?.map((item: MappedAddress) => ({
            label: item.formattedAddress,
            value: item,
          }))
        : [];
    } catch (error) {
      const parseError = t ? parseGqlError(error as any, t) : { message: 'Address not found' };
      const errorMessage = parseError.message;
      setError(errorMessage);
      onError?.(errorMessage);
      return [];
    }
  };

  const handleSelect = async (input: AutocompleteOption | null) => {
    onChange?.(input);

    if (input && input.value) {
      onSelect?.(input.value as MappedAddress);
    }
  };

  return (
    <Card
      {...styleBox}
      {...style}
    >
      <Autocomplete
        getOptions={handleSearch}
        isRequired={isRequired}
        isTruncated
        label={label || (t ? t('location.address-input-label') : 'Search for address')}
        leftElement={
          <SearchIcon
            color='primary.pure'
            boxSize={5}
          />
        }
        placeholder={
          placeholder ||
          (t ? t('location.address-input-placeholder') : 'Enter address or location')
        }
        rightElement={<GoogleOfficialIcon boxSize={4} />}
        initialValue={value || undefined}
        onChange={handleSelect}
        pr='4rem'
        bgColor='white'
        name='addressSearch'
        invalid={!!error}
        errors={error ? { addressSearch: error } : {}}
        helperText={helperText}
      />
    </Card>
  );
};

export default AddressSearch;
